import { Injectable, inject } from '@angular/core';
import { FiltersConfig } from '@dataclouder/ngx-core';
import { ILesson, ILessonTaken, LessonPrompts, LessonsAbstractService } from '@dataclouder/ngx-lessons';
import { getContentLessonGeneration, getDescriptionPrompt, getImageSuggestion } from 'src/app/pages/lessons/lesson-prompts';

import { Endpoints, LessonApi } from '../enums';
import { UserService } from './user.service';
import { HttpService } from '../system/http.service';
import { ToastService } from '../system/toast.service';

type LessonPaginator = { rows: ILesson[]; count: number };

@Injectable({
  providedIn: 'root',
})
export class LessonsService implements LessonsAbstractService {
  private httpService = inject(HttpService);
  private userService = inject(UserService);
  private toastrService = inject(ToastService);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
  updateLesson(lesson: ILesson): Promise<any> {
    // Kept 'any' as original, but ideally should be specific
    throw new Error('Method not implemented.');
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
  generateLesson(lesson: ILesson): Promise<any> {
    // Kept 'any' as original, but ideally should be specific
    throw new Error('Method not implemented.');
  }

  public async postLesson(lesson: ILesson) {
    const langParams = this.userService.getUserLangOptions();

    if (!lesson.appExtension?.['baseLang'] || !lesson.appExtension?.['targetLang']) {
      const settings = this.userService.getUserSnapshot().settings;
      lesson.appExtension = lesson.appExtension || {};

      lesson.appExtension['baseLang'] = settings.baseLanguage;
      lesson.appExtension['targetLang'] = settings.targetLanguage;
    }

    return this.httpService.postDataToService<ILesson>(`${Endpoints.Lessons.SaveLesson}?${langParams}`, lesson);
  }

  public async getLesson(id: string) {
    return this.httpService.getDataFromService<ILesson>(`${Endpoints.Lessons.Lesson}/${id}`);
  }

  public async getLessons(filterConfig: FiltersConfig = null, isPublished = true) {
    filterConfig.returnProps = {
      level: 1,
      title: 1,
      description: 1,
      createdDate: 1,
      banner: 1,
      metadata: 1,
      appExtensions: 1,
      media: 1,
    };
    const user = this.userService.getUserSnapshot();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let appFilters: Record<string, any> = { targetLang: user.settings.targetLanguage, baseLang: user.settings.baseLanguage, isPublished }; // Reverted to 'any' as per original code, consider specific type

    if (this.userService.isAdmin()) {
      this.toastrService.info('Eres administrador', 'puede ver todas las lecciones publicadas y no publicadas');
      appFilters = { targetLang: user.settings.targetLanguage, baseLang: user.settings.baseLanguage };
    }

    if (filterConfig) {
      filterConfig.filters = { ...appFilters, ...filterConfig.filters };
      filterConfig.sort = { level: 1, ...filterConfig.sort };
      return this.httpService.postDataToService<LessonPaginator>(Endpoints.Lessons.QueryLessons, filterConfig);
    } else {
      return this.httpService.postDataToService<LessonPaginator>(Endpoints.Lessons.QueryLessons, null);
    }
  }

  public async getPublicLessons() {
    return this.httpService.getDataFromService<ILesson[]>(Endpoints.Lessons.GetPublicLessons);
  }

  public async deleteLesson(id: string) {
    return this.httpService.deleteDataFromService(`${Endpoints.Lessons.DeleteLesson}/${id}`);
  }

  public async saveTakenLesson(lesson: ILessonTaken) {
    const lang = this.userService.getUserSnapshot().settings.targetLanguage;
    return this.httpService.putDataToService(`${Endpoints.Lessons.TakenLesson}?language=${lang}`, lesson);
  }

  public async generateAudiosForLesson(lessonId: string) {
    // TODO: get the data
    return this.httpService.getDataFromService(`${LessonApi.GenerateMedia}/${lessonId}`, 'python');
  }

  public async postGenerateByAI(lessonId: string) {
    return this.httpService.postDataToService(`${LessonApi.GenerateLesson}`, { id: lessonId }, 'python');
  }

  /**
   * Sends Markdown text to the backend for AI improvement.
   * @param lessonId The ID of the lesson.
   * @param markdownText The Markdown text to improve.
   * @returns Promise<unknown> The result from the backend API.
   */
  public async postImproveMDWithAI(lessonId: string, markdownText: string): Promise<unknown> {
    // TODO: Define LessonApi.ImproveMarkdown in your Endpoints enum
    // const endpoint = LessonApi.ImproveMarkdown; // Example endpoint
    const endpoint = `${Endpoints.Lessons.Base}/improve-markdown-ai`; // Placeholder endpoint - ADJUST AS NEEDED
    return this.httpService.postDataToService(endpoint, { id: lessonId, markdown: markdownText }, 'python'); // Assuming 'python' service alias like generateByAI
  }

  public extractTextFromHtml(html: string) {
    const r1 = new RegExp('~(.+?)~', 'g');

    const lessonHtml = html.replace(r1, (_matching, jsonCoded) => {
      const data = JSON.parse(jsonCoded);
      return `<span>${data?.settings?.text}</span>`;
    });
    console.log('lessonHtml', lessonHtml);
    const onlyText = this.extractTextFromHTML(lessonHtml);
    return onlyText;
  }

  private extractTextFromHTML(htmlString) {
    // Remove HTML tags
    let text = htmlString.replace(/<[^>]*>/g, ' ');

    // Remove style and script content
    text = text.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, ' ');
    text = text.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, ' ');

    // Decode HTML entities
    text = text.replace(/&nbsp;/g, ' ');
    text = text.replace(/&amp;/g, '&');
    text = text.replace(/&lt;/g, '<');
    text = text.replace(/&gt;/g, '>');

    // Remove extra whitespace
    text = text.replace(/\s+/g, ' ').trim();

    return text;
  }

  public getRecommendedLessons() {
    return this.httpService.getDataFromService<ILesson[]>(LessonApi.Recommendations);
  }

  // --- User Lesson Progress ---

  private readonly lessonProgressApiBase = '/api/lesson-progress'; // Base path for progress endpoints

  /**
   * Fetches the lesson progress for a specific user, optionally filtered by language.
   * Corresponds to: GET /api/lesson-progress/users/:userId?language=...
   * @param userId The ID of the user.
   * @param language Optional language code (e.g., 'en').
   * @returns Promise<unknown> containing user progress data (structure depends on backend implementation).
   */
  public async getUserLanguageProgress(userId: string, language?: string): Promise<unknown> {
    let url = `${this.lessonProgressApiBase}/users/${userId}`;
    if (language) {
      // Ensure language parameter is correctly encoded if it contains special characters
      url += `?language=${encodeURIComponent(language)}`;
    }
    // Using 'any' as return type because the backend response structure varies
    // and the 'all languages' case is marked as TODO in the backend controller.
    // Consider refining this type based on the actual backend implementation.
    // Using 'unknown' to satisfy linter, but backend response structure varies and has a TODO.
    return this.httpService.getDataFromService<unknown>(url);
  }

  /**
   * Fetches the progress for a specific lesson for a user in a given language.
   * Corresponds to: GET /api/lesson-progress/users/:userId/lessons/:lessonId?language=...
   * @param userId The ID of the user.
   * @param lessonId The ID of the lesson.
   * @param language The language code (e.g., 'en'). Required by the backend.
   * @returns Promise containing the specific lesson progress.
   */
  public async getUserLessonProgress(userId: string, lessonId: string, language: string): Promise<Omit<ILessonTaken, 'id'> & { id: string }> {
    // Backend requires language for specific lesson progress
    if (!language) {
      console.error('Language is required to get specific lesson progress.');
      // Consider throwing an error for clearer failure handling
      return Promise.reject(new Error('Language query parameter is required for getting specific lesson progress.'));
    }
    // Ensure language parameter is correctly encoded
    const encodedLanguage = encodeURIComponent(language);
    const url = `${this.lessonProgressApiBase}/users/${userId}/lessons/${lessonId}?language=${encodedLanguage}`;
    return this.httpService.getDataFromService<Omit<ILessonTaken, 'id'> & { id: string }>(url);
  }

  /**
   * Updates or creates (upserts) the progress for a specific lesson for a user in a given language.
   * Corresponds to: PUT /api/lesson-progress/users/:userId/lessons/:lessonId?language=...
   * @param userId The ID of the user.
   * @param lessonId The ID of the lesson.
   * @param language The language code (e.g., 'en'). Required by the backend.
   * @param progressData The data for the lesson progress to be saved (UpsertLessonProgressDto).
   * @returns Promise containing the result of the upsert operation from the backend.
   */
  public async upsertUserLessonProgress(
    userId: string,
    lessonId: string,
    language: string,
    progressData: ILessonTaken, // Using locally defined interface
  ): Promise<unknown> {
    // Backend requires language for specific lesson progress upsert
    if (!language) {
      console.error('Language is required to upsert specific lesson progress.');
      // Consider throwing an error for clearer failure handling
      return Promise.reject(new Error('Language query parameter is required for upserting specific lesson progress.'));
    }
    // Ensure language parameter is correctly encoded
    const encodedLanguage = encodeURIComponent(language);
    const url = `${this.lessonProgressApiBase}/users/${userId}/lessons/${lessonId}?language=${encodedLanguage}`;
    // Using 'unknown' as return type, refine based on actual backend response if needed.
    return this.httpService.putDataToService<unknown>(url, progressData);
  }

  getPrompts(): LessonPrompts {
    // this functionas assume iLesson is available
    return { description: getDescriptionPrompt, banner: getImageSuggestion, content: getContentLessonGeneration };
  }
}
